import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { ThreeColumnPage } from '../Components/ThreeColumnPage';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Alert,
  Button,
  CodeDisplay,
  Loading,
  useToast,
  Card,
  Heading,
} from '@aidkitorg/component-library';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { usePost } from '../API';
import { useParams, useHistory } from 'react-router-dom';
import { v4 } from 'uuid';
import { UserInfoContext } from '../Context';
import { useLocalizedStrings } from '../Localization';
import { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';
type IncomingMetadata = Record<string, { editable: boolean; value: string }>;
type SavedMetadata = Record<string, any>;
type EditableMetadataItem = { id: string; key: string; value: string };

const DELETE_ROLES = ['admin', 'program_admin'];

// Custom hook to manage organization metadata
export function useOrganizationMetadata(savedMetadata: SavedMetadata) {
  const [editableMetadataList, setEditableMetadataList] = useState<
    EditableMetadataItem[]
  >([]);

  // Process initial data from the API to separate editable and system metadata
  useEffect(() => {
    if (!savedMetadata) return;
    // User-editable metadata - stored as an array to preserve the order when editing keys
    const editableItems = Object.entries(savedMetadata).map(([key, value]) => ({
      id: v4(),
      key,
      value: String(value),
    }));
    setEditableMetadataList(editableItems);
  }, [savedMetadata]);

  const addField = useCallback(() => {
    setEditableMetadataList((prev) => [
      ...prev,
      { id: v4(), key: '', value: '' },
    ]);
  }, []);

  const removeField = useCallback((id: string) => {
    setEditableMetadataList((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateKey = useCallback((id: string, newKey: string) => {
    setEditableMetadataList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, key: newKey } : item))
    );
  }, []);

  const updateValue = useCallback((id: string, newValue: string) => {
    setEditableMetadataList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, value: newValue } : item))
    );
  }, []);

  // Get current editable metadata as simple object for making the update call
  const getCurrentEditableMetadata = useCallback((): SavedMetadata => {
    return editableMetadataList.reduce((acc, { key, value }) => {
      if (key.trim() !== '') acc[key.trim()] = value;
      return acc;
    }, {} as SavedMetadata);
  }, [editableMetadataList]);

  // For code display
  const formattedMetadata = useMemo(
    () => JSON.stringify(getCurrentEditableMetadata(), null, 2),
    [getCurrentEditableMetadata]
  );

  // For enabling / dissabling the save button
  const checkIfMetadataHasChanged = useCallback((originalEditableMetadata: SavedMetadata): boolean => {
    const currentEditableMetadata = getCurrentEditableMetadata();
    if (!originalEditableMetadata) {
      return Object.keys(currentEditableMetadata).length > 0;
    }
    return JSON.stringify(originalEditableMetadata) !== JSON.stringify(currentEditableMetadata)
  }, [getCurrentEditableMetadata]);

  return {
    editableMetadataList,
    formattedMetadata,
    getCurrentEditableMetadata,
    addField,
    removeField,
    updateKey,
    updateValue,
    checkIfMetadataHasChanged,
  };
}

function EditOrganization() {
  const [savedOrg, setSavedOrg] = useState<Organization>({ name: '', metadata: {}, quota: '', uid: ''});
  const [savedSystemMetadata, setSavedSystemMetadata] = useState<SavedMetadata>({});
  const [name, setName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { id } = useParams() as Record<string, string>;
  const { toast } = useToast();
  const history = useHistory();
  
  const getOrganization = usePost('/organization/get');
  const updateOrganization = usePost('/organization/update');
  const deleteOrganization = usePost('/organization/delete');
  const createOrganization = usePost('/organization/create');
  const L = useLocalizedStrings();
  const userInfo = useContext(UserInfoContext);

  useAsyncEffect(async () => {
    setIsLoading(true);
    if (id === 'new') {
      setSavedOrg({ name: '', metadata: {}, quota: '', uid: '' });
      setSavedSystemMetadata({});
      setName('');
    } else {
      const org = await getOrganization({ orgUid: id });
      if (org.result) {
        const incomingMetadata: IncomingMetadata = org.result.metadata || {};
        const editableMetadata: Record<string, any> = {};
        const systemMetadata: SavedMetadata = {};
        // Separate editable and system metadata and flatten the value
        for (const [key, val] of Object.entries(incomingMetadata)) {
          if (val?.editable) {
            editableMetadata[key] = String(val.value ?? '');
          } else {
            systemMetadata[key] = val?.value ?? val;
          }
        }

        setSavedOrg({ ...org.result, metadata: editableMetadata });
        setSavedSystemMetadata(systemMetadata);
        setName(org.result.name);
      // don't show an error message if a logged out user attempts to access this page
      } else if (!org || (org.error && org.error !== 'Route has not been exposed')) {
        toast({ variant: 'error', title: 'Failed to load organization'});
      }
    }
    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    if (savedSystemMetadata.deleted === 'true' || savedSystemMetadata.deleted === true) {
      setIsDeleted(true);
    }
  }, [savedSystemMetadata]);

  const {
    editableMetadataList,
    formattedMetadata,
    getCurrentEditableMetadata,
    addField,
    removeField,
    updateKey,
    updateValue,
    checkIfMetadataHasChanged,
  } = useOrganizationMetadata(savedOrg.metadata || {});

  const hasNameChanged = name.trim() !== savedOrg.name.trim();
  const hasMetadataChanged = !!savedOrg?.metadata ? checkIfMetadataHasChanged(savedOrg.metadata!) : false;

  const handleSaveOrganization = async() => {
    setIsSaving(true);
    let result;
    if (id === 'new') {
      result = await createOrganization({ name: name.trim(), metadata: JSON.stringify(getCurrentEditableMetadata()) });
      if (result?.success && result.uid) {
        if (result.created) {
          toast({
            variant: 'success',
            title: 'Organization created successfully',
          });
        } else {
          toast({
            variant: 'success',
            title: `Found existing organization with name: ${name.trim()}`,
          });
        }
        history.push(`/org/${result.uid}`);
      } else {
        toast({
          variant: 'error',
          title: 'Failed to create organization',
        });
      }
    } else {
      const updates: {
        orgUid: string;
        name?: string;
        metadata?: string;
      } = { orgUid: savedOrg.uid };
  
      if (hasNameChanged) {
        updates['name'] = name.trim();
      }
      if (hasMetadataChanged) {
        updates['metadata'] = JSON.stringify(getCurrentEditableMetadata());
      }
      result = await updateOrganization(updates);
    
      if (result?.success) {
        // Update the saved state to match current state
        setSavedOrg(prev => ({
          ...prev,
          name: updates.name || prev.name,
          metadata: getCurrentEditableMetadata(),
        }));
        toast({
          variant: 'success',
          title: 'Organization updated successfully',
        });
      } else {
        toast({
          variant: 'error',
          title: 'Failed to update organization',
        });
      }
    }
    setIsSaving(false);
  };

  const handleDeleteOrganization = async() => {
    const confirmed = await confirm(`Are you sure you want to delete ${savedOrg.name}?`);
    if (confirmed) {
      setIsDeleting(true);
      const result = await deleteOrganization({ orgUid: savedOrg.uid });
      if (result?.success) {
        setIsDeleted(true);
        toast({
          variant: 'success',
          title: 'Organization deleted successfully',
        });
      } else {
        toast({
          variant: 'error',
          title: 'Failed to delete organization',
        });
      }
    }
    setIsDeleting(false);
  };

  const hasDeletePerms = userInfo.tags?.some(item => DELETE_ROLES.includes(item));
  
  return (
    <Loading loading={isLoading} className="h-full flex flex-col justify-center">
      <Heading>{id === 'new' ? L.organization.create_new_organization : savedOrg.name}</Heading>
      <Card className="w-full">
        {isDeleted && (
          <div className="flex justify-start">
            <Alert variant="danger" className="mb-6 flex gap-3 w-full md:w-[647px]">
              <strong className="font-bold">{L.organization.organization_deleted}</strong>
            </Alert>
          </div>
        )}
        <label htmlFor="organizationName" className="mt-2 mb-1 font-bold">{L.organization.organization_name}</label>
        <input
          className="max-w-lg block w-full border p-2 mt-1 mb-1 sm:max-w-[647px] sm:text-sm rounded-md"
          id="organizationName"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Organization name"
          disabled={isDeleted}
        />
        {/* clicking on the label focuses on the first key */}
        <label htmlFor={`metadataKey-${editableMetadataList[0]?.id}`} className="mt-6 mb-1 font-bold">{L.organization.metadata}</label>
        {editableMetadataList.map(item => (
          <div key={item.id} className="flex gap-2">
            <input
              id={`metadataKey-${item.id}`}
              className="max-w-lg block w-full border p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md"
              value={item.key}
              onChange={(e) => updateKey(item.id, e.target.value)}
              placeholder="Key"
              aria-label="Metadata key"
              disabled={isDeleted}
            />
            <input
              id={`metadataValue-${item.id}`}
              className="max-w-lg block w-full border p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md"
              value={item.value}
              onChange={(e) => updateValue(item.id, e.target.value)}
              placeholder="Value"
              aria-label="Metadata value"
              disabled={isDeleted}
            />
            {!isDeleted && <button onClick={() => removeField(item.id)} className="text-red-500" aria-label="Remove metadata field">
              ✕
            </button>}
          </div>
        ))}
        {!isDeleted && (
          <div>
            <Button
              className="border px-3 py-2 mt-1"
              variant="secondary"
              onClick={addField}
            >
              + {L.organization.add_field}
            </Button>
          </div>
        )}

        {!isDeleted && (
          <div className="mt-10">
            <Accordion type="single">
              <AccordionItem className="max-w-[647px]" value="metadata-preview">
                <AccordionTrigger className="text-sm">{L.organization.metadata_preview}</AccordionTrigger>
                <AccordionContent>
                  <CodeDisplay
                    language="json"
                    code={formattedMetadata}
                    className="my-2"
                    variant="gray"
                    copyable={false}  
                    expandable={false}
                    showLineNumbers={false}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
        {!isDeleted && (
          <div className="mt-6 flex gap-2">
            <Button
              variant="primary"
              isDisabled={id === 'new' ? (!name.trim() || isSaving) : ((!hasNameChanged && !hasMetadataChanged) || isSaving)}
              loading={isSaving}
              onClick={handleSaveOrganization}
            >
              {id === 'new' ? L.organization.create_new_organization : L.organization.save_organization}
            </Button>
            {id !== 'new' && hasDeletePerms && <Button
              variant="danger"
              loading={isDeleting}
              onClick={handleDeleteOrganization}
            >
              {L.organization.delete_organization}
            </Button>}
          </div>
        )}
      </Card>
    </Loading>
  );
}

export default function OrganizationPage() {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const getNav = usePost("/navigation");

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (result?.navigation) {
      setNav(result.navigation);
    }
  }, []);
  
  return (
    <ThreeColumnPage
      nav={nav}
      isDashboard={true}
      title="Organization Info"
      main={<EditOrganization />}
    />
  );
}
